// ── VIDEO LINEUP — several videos of one kind, any mix of lengths ───────
//
// A quote used to carry at most ONE AI Motion video and ONE Cinematic video,
// each a single duration (`aiMotionDur` / `cinematicDur`). Bernard,
// 2026-09-24: reps need several of the same kind AND mixed lengths — e.g. two
// 60-second films plus a 30-second cut.
//
// Each kind is now a LIST of durations in seconds, e.g. [60, 60, 30],
// persisted as `inputs.aiMotionVideos` / `inputs.cinematicVideos` (community:
// `c_aiMotionVideos` / `c_cinematicVideos`) and copied to `calc_snapshot`.
// The kind's cost is the sum of each video at its duration's rate.
//
// `aiMotionDur` / `cinematicDur` survive as the LONGEST video in the list (or
// null when there is none). Everything that only asks "is there a video" or
// "how long is it" — the bundle module, the motion package, the timeline's
// duration row, older readers of saved quotes — keeps working off them, and
// a quote saved before this reads back as a one-video list.
//
// Mirrored in backend/services/video_lineup.py.

const toSeconds = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** The saved list, or — for a quote saved before lists existed — the single
 *  legacy duration as a one-video list. Anything unparseable is dropped. */
export function normalizeVideos(list, legacyDur = null) {
  if (Array.isArray(list)) return list.map(toSeconds).filter((s) => s !== null);
  const one = toSeconds(legacyDur);
  return one !== null ? [one] : [];
}

/** The longest video, or null — the `aiMotionDur` / `cinematicDur` value. */
export function primaryDuration(list) {
  const secs = normalizeVideos(list);
  return secs.length ? Math.max(...secs) : null;
}

/** Sum of each video at its duration's rate. */
export function videosCost(list, durationPrices) {
  return normalizeVideos(list).reduce(
    (sum, sec) => sum + (Number((durationPrices || {})[sec]) || 0), 0,
  );
}

/** { 60: 2, 30: 1 } — longest first when iterated via groupedVideos(). */
export function groupedVideos(list) {
  const counts = {};
  normalizeVideos(list).forEach((s) => { counts[s] = (counts[s] || 0) + 1; });
  return Object.keys(counts)
    .map(Number)
    .sort((a, b) => b - a)
    .map((sec) => ({ sec, qty: counts[sec] }));
}

/** Client-facing wording: "60-second production", or
 *  "2 × 60-second + 1 × 30-second productions". */
export function describeVideos(list) {
  const groups = groupedVideos(list);
  if (!groups.length) return "";
  const total = groups.reduce((s, g) => s + g.qty, 0);
  if (total === 1) return `${groups[0].sec}-second production`;
  return `${groups.map((g) => `${g.qty} × ${g.sec}-second`).join(" + ")} productions`;
}

/** Short sidebar wording: "60 sec" or "2 × 60 + 30 sec". */
export function shortVideos(list) {
  const groups = groupedVideos(list);
  if (!groups.length) return "";
  return `${groups.map((g) => (g.qty > 1 ? `${g.qty} × ${g.sec}` : `${g.sec}`)).join(" + ")} sec`;
}

export function videoCount(list) {
  return normalizeVideos(list).length;
}
